import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "@/store/use-page-title-store";
import { MyOrdersTab } from "@/features/orders/tabs/MyOrdersTab";
import { MyAddressesTab } from "@/features/orders/tabs/MyAddressesTab";
import { PreferencesTab } from "@/features/orders/preferences/PreferencesTab";
import { MySavedCardsTab } from "@/features/orders/tabs/MySavedCardsTab";
import { useOrdersStore } from "@/features/orders/store/orders-store";
import { cn } from "@/lib/utils";
import { BackButton } from "@/components/ui/back-button";

const OrdersPage: React.FC = () => {
  const { t } = useTranslation();
  const { activeTab, setActiveTab } = useOrdersStore();
  const { setPageTitle, clearPageTitle } = usePageTitle();

  useEffect(() => {
    setPageTitle("common.myAccount", "orders.list.subtitle");
    return () => clearPageTitle();
  }, [setPageTitle, clearPageTitle]);

  const tabs = [
    { key: "orders" as const, label: t("common.myOrders") },
    { key: "addresses" as const, label: t("common.myAddresses") },
    { key: "cards" as const, label: t("common.mySavedCards") },
    { key: "preferences" as const, label: t("common.myPreferences") },
  ];

  return (
    <div className="h-full overflow-y-auto bg-bg-primary dark:bg-bg-primary">
      {/* Header Section - Consistent with other pages */}
      <div className="w-full border-b border-border-secondary dark:border-border-secondary">
        <div className="pt-4 md:pt-6 pb-0 max-w-screen-2xl px-4 md:px-0 mx-auto">
          <div className="flex flex-col gap-2 md:gap-4">
            {/* Page Title */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <BackButton />
                <h1 className="text-lg md:text-xl lg:text-2xl font-semibold text-text-primary dark:text-text-primary leading-6 md:leading-7 lg:leading-8">
                  {t("common.myAccount")}
                </h1>
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="ml-8 flex gap-6 md:gap-12 overflow-x-auto">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                      "relative pb-4 text-xs md:text-sm font-semibold transition-colors whitespace-nowrap",
                      isActive
                        ? "text-indigo-500 dark:text-indigo-400"
                        : "text-text-secondary dark:text-gray-300 hover:text-text-primary dark:hover:text-gray-100",
                    )}
                  >
                    {tab.label}
                    {isActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 dark:bg-indigo-400" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Consistent padding */}
      <div className="px-4 md:px-16 py-4 md:py-6">
        <div className="flex flex-col gap-6 md:gap-8 max-w-screen-2xl mx-auto w-full">
          {/* Tab Content - White background with rounded corners */}
          <div className="rounded-2xl overflow-hidden border border-border-secondary dark:border-border-secondary">
            {activeTab === "orders" && <MyOrdersTab />}
            {activeTab === "addresses" && <MyAddressesTab />}
            {activeTab === "preferences" && <PreferencesTab />}
            {activeTab === "cards" && <MySavedCardsTab />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
